import { PDFDocument, PDFPage } from "pdf-lib"
import { pageHeight, pageWidth } from "@services/pdf/constants"
import { twoUnitsDimensionString } from "@services/pdf/dimensionFormat"
import { IFontSet } from "@services/pdf/fontSet"
import {
	drawRuns,
	gray,
	IRun,
	ITextStyle,
	measureHeight,
	paginateRuns,
	runsFromText,
	runsText,
} from "@services/pdf/textLayout"
import {
	IPdfOptions,
	IPdfPageData,
	showBrand,
	showExhibitions,
	showLiterature,
	showNote,
	showPrice,
	showProvenance,
	TPdfLayoutType,
} from "@services/pdf/types"
import { aspectFitSize, drawImageInTopRect, drawTemplateBackground, drawWhiteBackground, embedImage } from "./shared"

// iOS hardcodes these header literals in drawImageInfoPage (not localized there)
const provenanceHeader = "\nPROVENANCE"
const exhibitionHeader = "\nEXHIBITION HISTORY"
const literatureHeader = "\nLITERATURE"

/**
 * Port of iOS WorkPDFGenerator.drawImageInfoPage (general + gould layouts).
 * Draws onto `firstPage` and appends continuation pages to `doc` when the
 * description overflows (word-level pagination, template re-drawn per page).
 */
export async function drawImageInfoPages(
	doc: PDFDocument,
	firstPage: PDFPage,
	item: IPdfPageData,
	options: IPdfOptions,
	price: string,
	templateBytes: Uint8Array | null,
	layoutType: TPdfLayoutType,
	fonts: IFontSet,
	imageJpeg: Uint8Array | null
): Promise<void> {
	const width = pageWidth
	const height = pageHeight

	const family = fonts.contentFamily(layoutType)

	drawWhiteBackground(firstPage)
	if (showBrand(options)) {
		await drawTemplateBackground(doc, firstPage, templateBytes)
	}

	// page layout constants (iOS)
	const edgeHori = 0.1125 * width
	const headerHeight = 0.15 * height
	const footerHeight = 0.07 * height
	const spaceBelowImage = 40

	// text styles (iOS paragraph/lineSpacing settings)
	const artistStyle: ITextStyle = {
		font: fonts.resolve(family, "Semibold"),
		size: 12,
		lineSpacing: 15,
		align: "center",
	}
	const bodyStyle: ITextStyle = {
		font: fonts.resolve(family, "Regular"),
		size: 11,
		lineSpacing: 4,
		align: "center",
	}
	const titleStyle: ITextStyle = {
		font: fonts.resolve(family, "Italic"),
		size: 11,
		lineSpacing: 4,
		align: "center",
	}
	const sectionHeaderStyle: ITextStyle = {
		font: fonts.resolve(family, "Regular"),
		size: 10,
		lineSpacing: 4,
		align: "center",
		isSectionHeader: true,
	}

	// description content — exact iOS order and conditions
	const sizeDescription =
		item.width === "" || item.height === ""
			? ""
			: twoUnitsDimensionString(item.width, item.height, item.depth, item.unit)

	const titleYearString = [item.title, item.year].filter((s) => s !== "").join(", ")
	const titleYearRuns: IRun[] =
		titleYearString === ""
			? []
			: item.title !== "" && titleYearString.indexOf(item.title) === 0
			? [...runsFromText(item.title, titleStyle), ...runsFromText(titleYearString.slice(item.title.length), bodyStyle)]
			: runsFromText(titleYearString, bodyStyle)

	const parts: IRun[][] = [
		runsFromText(item.artist, artistStyle),
		titleYearRuns,
		runsFromText(item.medium, bodyStyle),
		runsFromText(sizeDescription, bodyStyle),
		runsFromText(item.otherDim, bodyStyle),
		runsFromText(item.edition, bodyStyle),
		runsFromText(showNote(options) ? item.note : "", bodyStyle),
		runsFromText(showPrice(options) && price !== "" ? `\n${price}` : "", bodyStyle),
		runsFromText(showProvenance(options) && item.provenance !== "" ? provenanceHeader : "", sectionHeaderStyle),
		runsFromText(showProvenance(options) ? item.provenance : "", bodyStyle),
		runsFromText(showExhibitions(options) && item.exhibition !== "" ? exhibitionHeader : "", sectionHeaderStyle),
		runsFromText(showExhibitions(options) ? item.exhibition : "", bodyStyle),
		runsFromText(showLiterature(options) && item.literature !== "" ? literatureHeader : "", sectionHeaderStyle),
		runsFromText(showLiterature(options) ? item.literature : "", bodyStyle),
	].filter((runs) => runsText(runs) !== "")

	const descRuns: IRun[] = []
	parts.forEach((part, i) => {
		descRuns.push(...part)
		if (i < parts.length - 1) {
			descRuns.push({ text: "\n", style: bodyStyle })
		}
	})

	// image area (iOS: max 75.5% width x 54% height at y = headerHeight, centered)
	const imageMaxWidth = 0.755 * width
	const imageMaxHeight = 0.54 * height
	const imageArea = {
		x: (width - imageMaxWidth) / 2,
		yTop: headerHeight,
		width: imageMaxWidth,
		height: imageMaxHeight,
	}

	let imageRectMaxY: number

	if (imageJpeg) {
		const image = await embedImage(doc, imageJpeg)
		const scaled = aspectFitSize({ width: image.width, height: image.height }, imageArea)
		const x = imageArea.x + (imageArea.width - scaled.width) / 2
		const yTop = imageArea.yTop + (imageArea.height - scaled.height) / 2
		drawImageInTopRect(firstPage, image, x, yTop, scaled.width, scaled.height)
		imageRectMaxY = yTop + scaled.height
	} else {
		imageRectMaxY = imageArea.yTop + imageArea.height
		// iOS always uses BeausiteDetail-Clear 11 gray for the NO IMAGE placeholder
		drawRuns(
			firstPage,
			runsFromText("NO IMAGE", {
				font: fonts.resolve("BeausiteDetail", "Clear"),
				size: 11,
				lineSpacing: 4,
				align: "center",
				color: gray,
			}),
			imageArea,
			"center"
		)
	}

	// first-page description rect, shortened by 50pt when the text overflows
	const firstPageDescRect = {
		x: edgeHori,
		yTop: imageRectMaxY + spaceBelowImage,
		width: width - edgeHori * 2,
		height: height - imageRectMaxY - footerHeight - spaceBelowImage,
	}
	const nextPageBuffer = 50

	const totalDescHeight = measureHeight(descRuns, firstPageDescRect.width)
	if (totalDescHeight > firstPageDescRect.height) {
		firstPageDescRect.height -= nextPageBuffer
	}

	const nextPageRect = {
		x: edgeHori,
		yTop: headerHeight,
		width: width - edgeHori * 2,
		height: height - headerHeight - footerHeight,
	}

	// collect pages synchronously, then draw (template embedding is async)
	const emitted: { pageRuns: IRun[]; pageNumber: number }[] = []
	paginateRuns(
		descRuns,
		(pageNumber) =>
			pageNumber === 1
				? { width: firstPageDescRect.width, height: firstPageDescRect.height }
				: { width: nextPageRect.width, height: nextPageRect.height },
		true, // header widow rule (iOS doubles section-header pieces in the fit test)
		(pageRuns, pageNumber) => emitted.push({ pageRuns, pageNumber })
	)

	for (const { pageRuns, pageNumber } of emitted) {
		let page = firstPage
		if (pageNumber > 1) {
			page = doc.addPage([width, height])
			drawWhiteBackground(page)
			if (showBrand(options)) {
				await drawTemplateBackground(doc, page, templateBytes)
			}
		}
		drawRuns(page, pageRuns, pageNumber === 1 ? firstPageDescRect : nextPageRect, "top")
	}
}
